/**
 * API functions for regulatory packages Backoffice.
 *
 * @taskId T657
 * @taskId T658
 * @see functional/Cycles/cycle-5/tasks.md HU-99-FE
 */

import { authFetch, normalizeApiError } from '@flexibilitypay/shared';

export type RegulatoryPackageStatus =
  | 'PENDING'
  | 'GENERATING'
  | 'COMPLETED'
  | 'FAILED'
  | 'WARNING'
  | 'BLOCKED';

export interface RegulatoryPackage {
  id: string;
  tenantId?: string;
  reportType?: string;
  periodYyyymm?: string;
  period?: string;
  status: RegulatoryPackageStatus;
  fileHashSha256?: string;
  createdAt?: string;
  completedAt?: string;
}

export interface RegulatoryPackagesParams {
  page: number;
  size: number;
  status?: RegulatoryPackageStatus;
}

export interface RegulatoryPackagesPage {
  content: RegulatoryPackage[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const BASE_PATH = '/api/v1/bo/regulatory-packages';
const STATUSES: readonly RegulatoryPackageStatus[] = [
  'PENDING',
  'GENERATING',
  'COMPLETED',
  'FAILED',
  'WARNING',
  'BLOCKED',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStatus(value: unknown): value is RegulatoryPackageStatus {
  return typeof value === 'string' && STATUSES.includes(value as RegulatoryPackageStatus);
}

function optionalString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function unwrapApiData(value: unknown): unknown {
  if (!isRecord(value)) return value;
  return 'data' in value ? value.data : value;
}

function normalizeItem(value: unknown): RegulatoryPackage | null {
  if (!isRecord(value)) return null;
  const id = optionalString(value.id);
  const status = value.status;
  if (!id || !isStatus(status)) return null;

  return {
    id,
    tenantId: optionalString(value.tenantId),
    reportType: optionalString(value.reportType),
    periodYyyymm: optionalString(value.periodYyyymm),
    period: optionalString(value.period),
    status,
    fileHashSha256: optionalString(value.fileHashSha256),
    createdAt: optionalString(value.createdAt),
    completedAt: optionalString(value.completedAt),
  };
}

function normalizeItems(value: unknown): RegulatoryPackage[] {
  if (!Array.isArray(value)) return [];
  return value.map(normalizeItem).filter((item): item is RegulatoryPackage => item !== null);
}

function normalizePage(raw: unknown, params: RegulatoryPackagesParams): RegulatoryPackagesPage {
  const value = unwrapApiData(raw);

  if (Array.isArray(value)) {
    const content = normalizeItems(value);
    return {
      content,
      page: params.page,
      size: params.size,
      totalElements: content.length,
      totalPages: content.length > 0 ? 1 : 0,
    };
  }

  if (!isRecord(value)) {
    return {
      content: [],
      page: params.page,
      size: params.size,
      totalElements: 0,
      totalPages: 0,
    };
  }

  const contentSource = Array.isArray(value.content) ? value.content : value.items;
  const content = normalizeItems(contentSource);
  const totalElements = optionalNumber(value.totalElements) ?? optionalNumber(value.total) ?? content.length;
  const totalPages = optionalNumber(value.totalPages) ?? Math.ceil(totalElements / Math.max(params.size, 1));

  return {
    content,
    page: optionalNumber(value.page) ?? optionalNumber(value.number) ?? params.page,
    size: optionalNumber(value.size) ?? params.size,
    totalElements,
    totalPages,
  };
}

export async function getRegulatoryPackages(
  params: RegulatoryPackagesParams,
): Promise<RegulatoryPackagesPage> {
  const searchParams = new URLSearchParams({
    page: String(params.page),
    size: String(params.size),
  });

  if (params.status) searchParams.set('status', params.status);

  const response = await authFetch(`${BASE_PATH}?${searchParams.toString()}`);
  if (!response.ok) throw await normalizeApiError(response);

  const raw: unknown = await response.json();
  return normalizePage(raw, params);
}

export async function downloadRegulatoryPackage(id: string): Promise<Blob> {
  const response = await authFetch(`${BASE_PATH}/${encodeURIComponent(id)}/download`);
  if (!response.ok) throw await normalizeApiError(response);
  return response.blob();
}